import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { motion } from 'framer-motion';
import { Search, Scale, ArrowRight } from 'lucide-react';
import { userService } from '../services/firebaseService';
import toast from 'react-hot-toast';

const RoleSelection = () => {
  const navigate = useNavigate();
  const { language, setUserRole, userRole } = useApp();
  const { user: currentUser, loading, signIn, signUp, signInWithGoogle } = useFirebaseAuth();
  
  // Role selection state
  const [selectedRole, setSelectedRole] = useState(null);

  const translations = {
    english: {
      title: 'Choose Your Role',
      subtitle: 'Select how you want to use NyaySahaya',
      findingLawyer: 'Finding a Lawyer',
      findingLawyerDesc: 'I need legal consultation and want to find qualified lawyers for my case',
      imLawyer: 'I\'m a Lawyer',
      imLawyerDesc: 'I want to register as a lawyer and provide legal services to clients',
      note: 'You can change your role later in the settings if needed'
    },
    hindi: {
      title: 'अपनी भूमिका चुनें',
      subtitle: 'चुनें कि आप NyaySahaya का उपयोग कैसे करना चाहते हैं',
      findingLawyer: 'वकील ढूंढ रहे हैं',
      findingLawyerDesc: 'मुझे कानूनी परामर्श की आवश्यकता है और मैं अपने मामले के लिए योग्य वकील ढूंढना चाहता हूं',
      imLawyer: 'मैं वकील हूं',
      imLawyerDesc: 'मैं वकील के रूप में पंजीकरण करना चाहता हूं और ग्राहकों को कानूनी सेवाएं प्रदान करना चाहता हूं',
      note: 'आप बाद में सेटिंग्स में अपनी भूमिका बदल सकते हैं यदि आवश्यक हो'
    }
  };

  const t = translations[language] || translations.english;

  // Handle authentication state changes
  useEffect(() => {
    if (currentUser?.uid && selectedRole) {
      console.log('🎯 User authenticated, handling navigation...');
      handleAuthenticatedUser(selectedRole);
    }
  }, [currentUser, selectedRole]);

  const handleRoleSelection = async (role) => {
    setSelectedRole(role);
    localStorage.setItem('selectedRole', role);
    setUserRole(role);
    
    // User should already be authenticated when they reach role selection
    if (currentUser) {
      await handleAuthenticatedUser(role);
    } else {
      // If somehow user is not authenticated, redirect to auth
      navigate('/auth');
    }
  };

  const handleAuthenticatedUser = async (role) => {
    try {
      if (!currentUser?.uid) {
        console.error('No authenticated user found');
        toast.error('Authentication failed. Please try again.');
        return;
      }

      console.log('✅ User authenticated, updating role:', { uid: currentUser.uid, role });
      
      // Save role to localStorage for consistency
      localStorage.setItem('selectedRole', role);
      
      // Update user profile with selected role in Firestore
      await userService.upsertUserProfile(currentUser.uid, { role });
      
      // Navigate based on role
      if (role === 'lawyer') {
        console.log('📝 Lawyer role selected, navigating to lawyer profile creation');
        navigate('/lawyer-profile');
      } else {
        console.log('📝 Client role selected, navigating to profile creation');
        navigate('/profile');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role. Please try again.');
    }
  };



  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }



  // Show role selection
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col justify-center items-center px-6 py-8 relative overflow-hidden">
      {/* Background blur effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-transparent to-lavender-100/20 dark:from-purple-900/10 dark:via-transparent dark:to-lavender-900/10 backdrop-blur-sm"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 text-center w-full max-w-sm mx-auto"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-10"
        >
          <div className="inline-flex items-center justify-center">
            <span className="text-3xl">🎗️</span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-3xl font-bold text-gray-900 dark:text-white mb-3"
        >
          {t.title}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-gray-600 dark:text-gray-400 mb-10 text-base"
        >
          {t.subtitle}
        </motion.p>

        {/* Role Cards Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="space-y-4 mb-10"
        >
          {/* Client Card */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            onClick={() => handleRoleSelection('client')}
            className="w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-gray-600 text-left p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors duration-200">
                <Search className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
                  {t.findingLawyer}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {t.findingLawyerDesc}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200" />
            </div>
          </motion.button>

          {/* Lawyer Card */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            onClick={() => handleRoleSelection('lawyer')}
            className="w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-gray-600 text-left p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors duration-200">
                <Scale className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                  {t.imLawyer}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {t.imLawyerDesc}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200" />
            </div>
          </motion.button>
        </motion.div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-xs text-gray-500 dark:text-gray-400 italic"
        >
          {t.note}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default RoleSelection;